import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";

/**
 * What the proposal editor shows when the negotiation could not be loaded and the failure was not a
 * settled "this proposal is gone" (that one ejects the viewer instead — see
 * `proposal-availability-error.ts`).
 *
 * It exists because the alternative was a loading spinner. A transient failure — a timeout on the
 * submission command's own refetch, a 500, a dropped connection — left the page rendering the same
 * indicator it shows while loading, forever, with nothing said and nothing to press (#4848). A spinner
 * that will never stop is worse than an error: the viewer waits instead of retrying, and cannot tell
 * a slow proposal from a broken one.
 */
const FailureContainer = styled(Container)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  height: "100%",
  justifyContent: "center",
  textAlign: "center",
}));

export type ProposalLoadFailureProps = {
  /** Runs the negotiation GET again. Absent when the page has nothing to retry with. */
  onRetry?: () => void;
  /** True while a retry is in flight, so the button cannot be pressed repeatedly. */
  isRetrying?: boolean;
};

export default function ProposalLoadFailure({ onRetry, isRetrying }: Readonly<ProposalLoadFailureProps>) {
  return (
    <FailureContainer>
      <Typography variant="h5">We couldn&apos;t load this proposal</Typography>
      <Typography variant="body1" color="text.secondary">
        The proposal is still there — the request to load it did not come back. Try again, and if it
        keeps failing our staff are looking into it.
      </Typography>
      {onRetry && (
        <Box>
          <Button variant="contained" onClick={onRetry} disabled={isRetrying}>
            {isRetrying ? "Retrying…" : "Try again"}
          </Button>
        </Box>
      )}
    </FailureContainer>
  );
}
